// FAR VIEW SHELL — spike prototype (w45 far-view-shell; APP_HORIZON §9 "Far view shell").
// Dev only. main.js calls farviewDraw() in place of its flat main render; without ?farview=1 / ?seam=1 it returns
// false at once (one flag test). ?seam=1 installs window.__farview.arm (the seam lab's `farview-*` arms,
// tools/seamlab/arms.json); ?farview=1[&fvD=m&fvStep=m] draws the flat main view with a runtime shell.
//
// The idea: everything beyond a distance D is replaced by a PARALLAX-CORRECT picture of the
// game's own render from a cell centre — colour + depth captured by the game's renderer (same
// programs, same tone map + encode: same colour by construction), turned into a depth-displaced
// grid mesh (the "shell": one grid per capture face, each vertex pushed out to the NEAREST depth
// of its footprint, the colour re-projected per fragment through the capture camera). The near
// scene is drawn with the camera's far plane at D after the shell, on a cleared depth buffer.
//
//   capture({ x, y, z, yaw, D, faces, spacing, hfov, vfov, ppd, gstep })  → stats
//   begin(cam) / end(cam)   around ONE render of the scene with `cam` (the shell drawn first,
//                           depth cleared, cam.far = D for the rest; the sky + the ring's
//                           un-culled meshes hidden — they are in the shell)
//   auto(cam, opts)         the runtime variant: re-capture when the eye left the cell (step m)
//   state(), dispose()
//
// No invention: the shell is a render of the live scene, never a painted or photo sky.

const DEG = Math.PI / 180;

// P1 (switch < 0.5 device px): the largest eye offset (m) from the capture point at which a single-viewpoint far
// view with everything at ≥ D m errs by < 0.5 px at `ppd` px / degree — the disocclusion band o·(1/d1 − 1/d2) ≤ o/D
export const p1Offset = (D, ppd) => 0.5 * (DEG / ppd) * D;

// the capture faces of one cell (pure; scripts/check-farview.mjs): W × H px per face at `ppd` in the face centre,
// the planar near plane (everything at radial < D − margin from the cell is OUT of the capture — the near pass
// draws it; cf turns radial into face depth at the corner), the grid (one vertex per `gstep` px), tris, MB
export function facePlan(o) {
  const D = o.D ?? 2000, nF = o.faces ?? 3, sp = (o.spacing ?? 60) * DEG;
  const hf = (o.hfov ?? 64) * DEG, vf = (o.vfov ?? 70) * DEG, ppd = o.ppd ?? 25, step = o.gstep ?? 16;
  const tH = Math.tan(hf / 2), tV = Math.tan(vf / 2);
  const W = Math.round(2 * ppd / DEG * tH), H = Math.round(2 * ppd / DEG * tV);
  const cf = Math.sqrt(1 + tH * tH + tV * tV), near = Math.max(1, (D - (o.margin ?? 0)) / cf);
  const gx = Math.floor(W / step) + 1 + (W % step ? 1 : 0), gy = Math.floor(H / step) + 1 + (H % step ? 1 : 0);
  const tris = nF * (gx - 1) * (gy - 1) * 2;
  const texMB = +(nF * W * H * 4 * 4 / 3 / 1048576).toFixed(1);   // RGBA8 + mips, uncompressed
  return { D, nF, sp, hf, vf, tH, tV, W, H, cf, near, step, gx, gy, tris, texMB };
}

export function createFarView(THREE, renderer, scene) {
  const faces = [];       // { cam, rt, dmin, mesh, W, H, gx, gy }
  const group = new THREE.Group();
  group.name = 'farview';
  group.renderOrder = 95;   // after the opaque near scene (early-z rejects the shell behind it), before the sky (100)
  let cell = null, hidden = [], on = false, stats = null;
  const _s = new THREE.Sphere(), _p = new THREE.Vector3();
  const uCell = { value: new THREE.Vector3() }, uPush = { value: 1.01 };

  // the depth-min reduction: one texel per grid vertex = the nearest depth of its footprint
  const redMat = new THREE.ShaderMaterial({
    uniforms: { uDepth: { value: null }, uSize: { value: new THREE.Vector2() }, uStep: { value: 16 } },
    vertexShader: 'void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }',
    fragmentShader: `
      precision highp float;
      uniform highp sampler2D uDepth; uniform vec2 uSize; uniform float uStep;
      void main() {
        ivec2 g = ivec2(gl_FragCoord.xy);
        vec2 c = min(vec2(g) * uStep, uSize - 1.0);
        float h = uStep * 0.5, m = 1.0;
        for (float dy = -h; dy <= h; dy += 1.0) for (float dx = -h; dx <= h; dx += 1.0) {
          ivec2 p = ivec2(clamp(c + vec2(dx, dy), vec2(0.0), uSize - 1.0));
          m = min(m, texelFetch(uDepth, p, 0).r);
        }
        gl_FragColor = vec4(m, 0.0, 0.0, 1.0);
      }`,
    depthTest: false, depthWrite: false,
  });
  const redScene = new THREE.Scene();
  const redQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), redMat);
  redQuad.frustumCulled = false;
  redScene.add(redQuad);
  const redCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  // the shell: drawn with the frame's OWN camera at its TRUE depth, pushed back by uPush (1 %: the near copy of a
  // surface that crosses D wins the depth test; 24-bit depth resolves 1 % to ~10 km at near 0.08), so the live sky
  // (behind), the live clouds and every near object depth-test against it as against the real far field.
  // Texels with no capture content (sky, under the capture's near plane) are discarded: the live sky shows there.
  const shellMat = (f) => new THREE.ShaderMaterial({
    uniforms: {
      uDmin: { value: f.dmin.texture }, uColor: { value: f.rt.texture }, uDepth: { value: f.rt.depthTexture },
      uCapVP: { value: new THREE.Matrix4() }, uCapVPInv: { value: new THREE.Matrix4() },
      uCell, uPush, uFlat: { value: 0 }, uSize: { value: new THREE.Vector2(f.W, f.H) },
    },
    vertexShader: `
      precision highp float;
      in vec2 gi; in vec2 gu;
      uniform highp sampler2D uDmin; uniform mat4 uCapVPInv; uniform vec3 uCell; uniform float uPush;
      out vec3 vW;
      void main() {
        float d = min(texelFetch(uDmin, ivec2(gi), 0).r, 0.9999999);   // the sky: just inside the capture's far plane
        vec4 w = uCapVPInv * vec4(gu * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
        vW = w.xyz / w.w;
        gl_Position = projectionMatrix * viewMatrix * vec4(uCell + (vW - uCell) * uPush, 1.0);
      }`,
    fragmentShader: `
      precision highp float;
      uniform mat4 uCapVP; uniform sampler2D uColor; uniform highp sampler2D uDepth; uniform vec2 uSize; uniform float uFlat;
      in vec3 vW;
      void main() {
        vec4 p = uCapVP * vec4(vW, 1.0);
        vec2 uv = p.xy / p.w * 0.5 + 0.5;
        if (texelFetch(uDepth, ivec2(clamp(uv * uSize, vec2(0.0), uSize - 1.0)), 0).r >= 0.9999999) discard;
        if (uFlat > 0.5) { gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0); return; }
        gl_FragColor = vec4(texture(uColor, uv).rgb, 1.0);   // the capture's display-encoded bytes, passed through
      }`,
    depthTest: true, depthWrite: true, side: THREE.DoubleSide,
  });

  function freeFaces() {
    for (const f of faces) {
      f.rt.dispose(); f.rt.depthTexture.dispose(); f.dmin.dispose();
      f.mesh.geometry.dispose(); f.mesh.material.dispose(); group.remove(f.mesh);
    }
    faces.length = 0;
  }

  function gridGeom(gx, gy, W, H, step) {
    const n = gx * gy, gi = new Float32Array(n * 2), gu = new Float32Array(n * 2), pos = new Float32Array(n * 3);
    for (let j = 0; j < gy; j++) for (let i = 0; i < gx; i++) {
      const k = j * gx + i;
      gi[k * 2] = i; gi[k * 2 + 1] = j;
      gu[k * 2] = Math.min(i * step, W) / W; gu[k * 2 + 1] = Math.min(j * step, H) / H;
    }
    const idx = new Uint32Array((gx - 1) * (gy - 1) * 6);
    let q = 0;
    for (let j = 0; j < gy - 1; j++) for (let i = 0; i < gx - 1; i++) {
      const a = j * gx + i, b = a + 1, c = a + gx, d = c + 1;
      idx[q++] = a; idx[q++] = b; idx[q++] = d; idx[q++] = a; idx[q++] = d; idx[q++] = c;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));   // unused (three wants one)
    g.setAttribute('gi', new THREE.BufferAttribute(gi, 2));
    g.setAttribute('gu', new THREE.BufferAttribute(gu, 2));
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    return g;
  }

  // what never goes into the shell: every mesh three may not cull (frustumCulled false: the sky dome, the clouds,
  // lift chairs, avatars, furniture …) — the sky and the movers stay live; the rest of that set is near anyway
  function hideLive() {
    const h = [];
    scene.traverse((o) => { if (o.visible && (o.isMesh || o.isInstancedMesh || o.isLine || o.isPoints) && o.frustumCulled === false && o.parent !== group) { o.visible = false; h.push(o); } });
    return h;
  }

  // capture the far view from a cell centre. yaw = compass heading of the middle face (deg, 0 = −z, clockwise)
  function capture(o) {
    const t0 = performance.now();
    const P = facePlan(o), { D, nF, sp, vf, tH, tV, W, H, step, near } = P;
    if (faces.length !== nF || !faces[0] || faces[0].W !== W || faces[0].H !== H || faces[0].step !== step) {
      freeFaces();
      for (let k = 0; k < nF; k++) {
        const rt = new THREE.WebGLRenderTarget(W, H, {
          samples: 0, depthTexture: new THREE.DepthTexture(W, H, THREE.FloatType),
          generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter,
        });
        rt.texture.colorSpace = THREE.SRGBColorSpace;
        rt.isXRRenderTarget = true;            // tone map + encode exactly as for the canvas (the lab's targets do the same)
        rt.texture.internalFormat = 'RGBA8';   // stored = display bytes; the shell passes them through
        const { gx, gy } = P;
        const dmin = new THREE.WebGLRenderTarget(gx, gy, { type: THREE.FloatType, format: THREE.RedFormat,
          depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false });
        const cam = new THREE.PerspectiveCamera(vf / DEG, tH / tV, near, 130000);
        cam.rotation.order = 'YXZ';
        const f = { cam, rt, dmin, W, H, gx, gy, step };
        f.mesh = new THREE.Mesh(gridGeom(gx, gy, W, H, step), shellMat(f));
        f.mesh.frustumCulled = false;
        faces.push(f);
        group.add(f.mesh);
      }
    }
    const prevRT = renderer.getRenderTarget();
    const live = hideLive();
    const yaw0 = (o.yaw ?? 0) * DEG;
    try {
      for (let k = 0; k < nF; k++) {
        const f = faces[k], c = f.cam;
        c.near = near; c.far = 130000; c.updateProjectionMatrix();
        c.position.set(o.x, o.y, o.z);
        c.rotation.set((o.pitch ?? 0) * DEG, -(yaw0 + (k - (nF - 1) / 2) * sp), 0);
        if (o.layers != null) c.layers.mask = o.layers;
        c.updateMatrixWorld(true);
        renderer.setRenderTarget(f.rt);
        renderer.render(scene, c);
        renderer.setRenderTarget(null);   // resolves + mipmaps
        redMat.uniforms.uDepth.value = f.rt.depthTexture;
        redMat.uniforms.uSize.value.set(W, H);
        redMat.uniforms.uStep.value = step;
        renderer.setRenderTarget(f.dmin);
        renderer.render(redScene, redCam);
        renderer.setRenderTarget(null);
        const vp = f.mesh.material.uniforms.uCapVP.value.multiplyMatrices(c.projectionMatrix, c.matrixWorldInverse);
        f.mesh.material.uniforms.uCapVPInv.value.copy(vp).invert();
      }
    } finally { for (const x of live) x.visible = true; }
    // diagnostics: the middle face's depth grid (min / max of the reconstructed distance, the empty share)
    const fm = faces[nF >> 1], gl = renderer.getContext(), buf = new Float32Array(fm.gx * fm.gy * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, renderer.properties.get(fm.dmin).__webglFramebuffer);
    gl.readPixels(0, 0, fm.gx, fm.gy, gl.RGBA, gl.FLOAT, buf);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    let dLo = Infinity, dHi = -Infinity, sky = 0;
    for (let k = 0; k < fm.gx * fm.gy; k++) {
      const d = buf[k * 4];
      if (d >= 0.9999999) { sky++; continue; }
      const z = 2 * near * 130000 / (130000 + near - (2 * d - 1) * (130000 - near));
      if (z < dLo) dLo = z; if (z > dHi) dHi = z;
    }
    renderer.setRenderTarget(prevRT);
    renderer.resetState();
    cell = { x: o.x, y: o.y, z: o.z, D, near };
    uCell.value.set(o.x, o.y, o.z);
    stats = { ms: +(performance.now() - t0).toFixed(1), W, H, faces: nF, gx: P.gx, gy: P.gy, tris: P.tris, draws: nF,
      texMB: P.texMB, near: +near.toFixed(1), D, live: live.length,
      diag: { dist_min: Math.round(dLo), dist_max: Math.round(dHi), empty: +(sky / (fm.gx * fm.gy)).toFixed(3) } };
    return stats;
  }

  // around one render of `scene` with `cam`: every cullable unit whose bounding sphere lies wholly beyond D from the
  // eye is hidden (what the shell replaces: no clip plane, no depth clear), the shell joins the scene
  function begin(cam, bo = {}) {
    if (!cell) return false;
    const cut = bo.cut !== false, shell = bo.shell !== false;
    hidden = [];
    if (cut) {
      const e = cam.getWorldPosition(_p), D = cell.D;
      scene.traverse((o) => {
        if (!o.visible || o.frustumCulled === false || !(o.isMesh || o.isInstancedMesh || o.isLine || o.isPoints)) return;
        if (o.isInstancedMesh) { if (!o.boundingSphere) o.computeBoundingSphere(); _s.copy(o.boundingSphere); }
        else { const g = o.geometry; if (!g) return; if (!g.boundingSphere) g.computeBoundingSphere(); _s.copy(g.boundingSphere); }
        _s.applyMatrix4(o.matrixWorld);
        if (Number.isFinite(_s.radius) && _s.center.distanceTo(e) - _s.radius > D) { o.visible = false; hidden.push(o); }
      });
    }
    for (const f of faces) f.mesh.material.uniforms.uFlat.value = bo.flat ? 1 : 0;
    uPush.value = bo.push ?? 1.01;
    if (shell) scene.add(group);
    on = true;
    return true;
  }
  function end() {
    if (!on) return;
    for (const o of hidden) o.visible = true;
    hidden = []; on = false;
    scene.remove(group);
  }
  // the runtime variant: a fresh capture when the eye is `step` m from the cell (or none yet)
  function auto(cam, o) {
    const p = cam.getWorldPosition(new THREE.Vector3());
    const moved = cell ? Math.hypot(p.x - cell.x, p.y - cell.y, p.z - cell.z) : Infinity;
    if (moved < (o.step ?? 4)) return null;
    const dir = cam.getWorldDirection(new THREE.Vector3());
    const yaw = Math.atan2(dir.x, -dir.z) / DEG;
    return capture({ ...o, x: p.x, y: p.y, z: p.z, yaw, layers: cam.layers.mask });
  }
  return {
    capture, begin, end, auto,
    state: () => ({ cell, stats, hidden: hidden.length }),
    dispose() { freeFaces(); redMat.dispose(); redQuad.geometry.dispose(); cell = null; },
  };
}

// the seam lab's arm around ONE farview instance (tools/seamlab/arms.json `farview-*`; c = the lab's
// candidate context: i, n, j (−1 = the main capture), cam, scene, note). a.mode: 'cell' = one capture at the
// nearest of a.cells [[x, z, yaw]] (the eye's y at the route's first frame) — the baked-cell model;
// 'dyn' = auto() on Δpos a.step — the runtime model; 'none' = the current look.
// a.diag: every 30th frame the pose is rendered into a diagW × diagH target (default 2420 × 1361 ≈ the Quest's
// two eyes at scale 0.6) once per VARIANT, each a TIME_ELAPSED query after a drain (a 1 px read-back: nothing
// queued is counted — a query around the lab's own candidate render counted its queued passes); the results
// are read in later frames (WebGL hands them out only after the task) and land in one note at the last frame.
export function labArm(fv, renderer, a) {
  const gl = renderer.getContext(), ext = gl.getExtension('EXT_disjoint_timer_query_webgl2');
  const caps = [], pend = [], res = {}, geo = {}, px = new Uint8Array(4);
  let rt = null, lost = 0;
  const on = a.mode === 'cell' || a.mode === 'dyn';
  const VARS = { plain: null, cut: { shell: false }, full: {}, fullFlat: { flat: true } };
  function poll() {
    if (!ext) return;
    if (gl.getParameter(ext.GPU_DISJOINT_EXT)) { lost += pend.length; pend.forEach((x) => gl.deleteQuery(x.q)); pend.length = 0; return; }
    while (pend.length && gl.getQueryParameter(pend[0].q, gl.QUERY_RESULT_AVAILABLE)) {
      const x = pend.shift();
      (res[x.k] ||= []).push(gl.getQueryParameter(x.q, gl.QUERY_RESULT) / 1e6);
      gl.deleteQuery(x.q);
    }
  }
  function diagTimes(c) {
    if (!ext) return;
    const T = window.__seamThree, W = a.diagW || 2420, H = a.diagH || 1361;
    if (!rt) {
      rt = new T.WebGLRenderTarget(W, H, { samples: 0, depthTexture: new T.DepthTexture(W, H, T.UnsignedIntType) });
      rt.texture.colorSpace = T.SRGBColorSpace; rt.isXRRenderTarget = true; rt.texture.internalFormat = 'RGBA8';
    }
    for (let rep = 0; rep < 2; rep++) for (const [k, bo] of Object.entries(VARS)) {
      if (bo && !fv.state().cell) continue;
      if (bo) fv.begin(c.cam, bo);
      renderer.setRenderTarget(rt);
      gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);   // the drain
      const q = gl.createQuery();
      gl.beginQuery(ext.TIME_ELAPSED_EXT, q);
      renderer.render(c.scene, c.cam);
      gl.endQuery(ext.TIME_ELAPSED_EXT);
      if (rep) { const ri = renderer.info.render; (geo[k] ||= []).push([ri.triangles, ri.calls]); }
      renderer.setRenderTarget(null);
      if (bo) fv.end(c.cam);
      if (rep) pend.push({ k, q }); else gl.deleteQuery(q);   // rep 0 warms (programs, uploads)
    }
    renderer.resetState();
  }
  const med = (v) => { const s = [...v].sort((x, y) => x - y); return +s[s.length >> 1].toFixed(2); };
  return {
    before(c) {
      if (c.j === -1) {
        poll();
        if (a.mode === 'cell') {
          const p = c.cam.position;
          let best = null, bd = Infinity;
          for (const k of a.cells || []) { const d = Math.hypot(k[0] - p.x, k[1] - p.z); if (d < bd) { bd = d; best = k; } }
          const cur = fv.state().cell;
          if (best && (!cur || Math.hypot(cur.x - best[0], cur.z - best[1]) > 1)) {
            caps.push(fv.capture({ ...a, x: best[0], y: p.y, z: best[1], yaw: best[2], layers: c.cam.layers.mask }));
          }
        } else if (a.mode === 'dyn') {
          const s = fv.auto(c.cam, a);
          if (s) caps.push(s);
        }
        if (a.diag && c.i % 30 === 0 && c.i < c.n - 2) diagTimes(c);
      }
      if (on) fv.begin(c.cam, a);
    },
    after(c) {
      if (on) fv.end(c.cam);
      if (c.j === -1 && c.i === c.n - 1) {
        poll();
        const t = {};
        for (const k in res) t[k] = { med: med(res[k]), n: res[k].length };
        for (const k in geo) Object.assign(t[k] ||= {}, { tris: Math.round(geo[k].reduce((x, y) => x + y[0], 0) / geo[k].length), calls: Math.round(geo[k].reduce((x, y) => x + y[1], 0) / geo[k].length) });
        c.note(`${a.id || a.mode}: gpu_ms ${JSON.stringify(t)} lost ${lost + pend.length} | captures ${caps.length}`
          + ` ms ${JSON.stringify(caps.map((x) => x.ms).slice(0, 6))} | ${caps.length ? JSON.stringify(caps[caps.length - 1]) : ''}`);
        if (rt) { rt.dispose(); rt.depthTexture.dispose(); rt = null; }
      }
    },
  };
}

// the one hook main.js calls (its flat main render): false = not drawn here, main.js renders as always
const _q = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
const MAIN = _q && _q.get('farview') === '1'
  ? { D: +(_q.get('fvD') || 2000), step: +(_q.get('fvStep') || 4), faces: 6, spacing: 60, hfov: 64, vfov: 90, ppd: 20 } : null;
const LAB = !!(_q && _q.get('seam') === '1');
let _main = null;
const _arms = new Map();
export function farviewDraw(THREE, renderer, scene, camera, mode) {
  if (!MAIN && !LAB) return false;
  if (LAB && !window.__farview) {
    window.__farview = { arm: (a) => {
      if (!_arms.has(a.id)) _arms.set(a.id, createFarView(THREE, renderer, scene));
      return labArm(_arms.get(a.id), renderer, a);
    } };
  }
  if (!MAIN || mode !== 'flat' || renderer.xr.isPresenting) return false;
  const fv = _main || (_main = createFarView(THREE, renderer, scene));
  fv.auto(camera, MAIN);
  fv.begin(camera);
  try { renderer.render(scene, camera); } finally { fv.end(camera); }
  return true;
}
